import { useQuery } from '@tanstack/react-query';
import type { TableOrderInterface } from '@uefadigital/panenka';
import { MatchUnitSize } from '@uefadigital/panenka';
import {
  PkTable,
  PkTableBody,
  PkTableCell,
  PkTableHeader,
  PkTableHeaderCol,
  PkTableRow,
} from '@uefadigital/panenka-react';
import { memo, useState, useMemo } from 'react';

import { useWindowSize } from '@hooks/useWindowSize';
import { getMatchesQuery } from '@queries/matches';
import { hydrateWithTranslations } from '@services/TranslatorService';
import MatchUnit from '@shared/MatchUnit';
import { useTranslateFn } from '@shared/Translate';
import { Order } from '@shared-types/common.types';
import type { Match } from '@shared-types/match.types';
import { RoundMode } from '@shared-types/match.types';
import { orderBy } from '@utilities/collection-utils';
import { dateToString } from '@utilities/dateTime';

import './styles.scss';

export interface FinalsOptions {
  cupId: string;
  showTournamentEdition: boolean;
  showHighlights?: boolean;
  disableTeamsLink?: boolean;
  hideDate?: boolean;
  hideRound?: boolean;
  hideContext?: boolean;
  hideSeeMore?: boolean;
}

const sortFunctions = {
  tournament: (m: Match) => m.seasonYear,
  venue: (m: Match) => hydrateWithTranslations(m.stadium).name,
} as const;

const Finals = ({ options }: { options: FinalsOptions }) => {
  const t = useTranslateFn();
  const [sortColumn, setSortColumn] = useState<string>(null);
  const [order, setOrder] = useState<Order>(Order.DESC);

  const { data: matches } = useQuery({
    ...getMatchesQuery({
      competitionId: options.cupId,
      roundMode: RoundMode.FINAL,
      seasonsRange: 'HISTORICAL',
      order: Order.ASC,
      offset: 0,
      limit: 100,
    }),
    enabled: !!options.cupId,
  });

  const sortedMatches = useMemo(() => {
    if (!matches) return null;
    let sorted = matches;

    if (sortColumn && sortFunctions[sortColumn]) {
      sorted = orderBy(matches, sortFunctions[sortColumn]);
    }
    return order === Order.ASC ? sorted : sorted.reverse();
  }, [order, sortColumn, matches]);

  const handleSortingChange = (event: CustomEvent<{ columnKey: string | number; order: TableOrderInterface }>) => {
    setSortColumn(`${event.detail.columnKey}`);
    setOrder(event.detail.order as Order);
  };

  const getMatchYear = (matchDate: string) => {
    return Number(dateToString(new Date(matchDate), 'YYYY'));
  };

  const { isDesktop } = useWindowSize();

  return (
    <div pk-theme='dark'>
      <PkTable
        rowSpacing={isDesktop ? 0 : 16}
        rowDivider
        sortBy='tournament'
        sortOrder='DESC'
        onPkTableSortBy={handleSortingChange}
      >
        <PkTableHeader className='header'>
          <PkTableHeaderCol columnKey='tournament' sortable aligned='center' className='header-column' width='150'>
            {t(options?.showTournamentEdition ? 'nav_tournaments' : 'season')}
          </PkTableHeaderCol>
          <PkTableHeaderCol
            columnKey='finalDate'
            aligned='center'
            width='120'
            className='header-column header-tournaments'
            canGrow
          />
          <PkTableHeaderCol
            columnKey='match'
            aligned='center'
            className='header-column'
            usePercentageWidth
            width='40'
            canShrink
          >
            {t('nav_match')}
          </PkTableHeaderCol>
          <PkTableHeaderCol columnKey='venue' aligned='center' sortable className='header-column' width='250'>
            {t('tckt_venue')}
          </PkTableHeaderCol>
        </PkTableHeader>
        <PkTableBody pk-theme='light'>
          {sortedMatches?.map((match) => (
            <PkTableRow key={`row_${match.id}`} className='table-row'>
              <PkTableCell columnKey='tournament' aligned='center' className='table-cell table-cell__tournament'>
                {options?.showTournamentEdition ? (
                  <div className={`season-logo season_${options?.cupId === '3' ? match.seasonYear : 'no-logo'}`}>
                    <div className='season-year'>{match.seasonYear}</div>
                  </div>
                ) : (
                  <span>
                    {getMatchYear(match.kickOffTime.dateTime) - 1}/
                    {getMatchYear(match.kickOffTime.dateTime).toString().substring(2, 4)}
                  </span>
                )}
              </PkTableCell>
              <PkTableCell columnKey='finalDate' aligned='center' className='table-cell table-cell__final-date'>
                {dateToString(new Date(match.kickOffTime?.dateTime ?? match.kickOffTime?.date), 'D MMMM YYYY')}
              </PkTableCell>
              <PkTableCell columnKey='match' aligned='center' className='table-cell table-cell__match'>
                <MatchUnit match={match} size={MatchUnitSize.M} options={options} />
              </PkTableCell>
              <PkTableCell columnKey='venue' aligned='center' className='table-cell table-cell__venue-info'>
                <div className='table-cell__venue-info--stadium'>{hydrateWithTranslations(match.stadium).name}</div>
                <div className='table-cell__venue-info--city'>{hydrateWithTranslations(match.stadium.city).name}</div>
              </PkTableCell>
            </PkTableRow>
          ))}
        </PkTableBody>
      </PkTable>
    </div>
  );
};

export default memo(Finals);
